<template>
  <div>
    <section class="form-section">
      <h2 class="section-title">{{ adminText('workflowsAll.event_cover_image_ecdd425', 'Event cover image') }}</h2>
      <div class="section-card preview-layout">
        <div class="preview-layout__settings">
          <cover-field-vertical
            v-model="form.welcomeBackground"
            :cropper="false"
            labelled-actions
            :description="adminText('workflowsAll.image_with_a_portrait_9_16_aspect_ratio_and_n_9ed0591', 'Image with a portrait (9:16) aspect ratio and not larger than 10 MB is recommended.')"
          />
        </div>
        <div class="preview-layout__preview">
          <p class="block-label block-label--preview">{{ adminText('workflowsAll.cover_preview_a6cc4d4', 'Cover preview') }}</p>
          <cover-preview
            :image="form.welcomeBackground"
            :title="workflowLabel(form.welcomeTitle)"
            :primary-color="form.primaryColor"
          />
        </div>
      </div>
    </section>

    <section class="form-section">
      <text-field
        v-model.trim="form.welcomeTitle"
        :validator="vs.form.welcomeTitle"
        :label="adminText('workflowsAll.title_db83dc1', 'Title*')"
        autofocus
      />
      <textarea-field
        v-model.trim="form.welcomeSubtitle"
        :validator="vs.form.welcomeSubtitle"
        :label="adminText('workflowsAll.subtitle_2b907f1', 'Subtitle*')"
      />
    </section>

    <section class="form-section">
      <h2 class="section-title">{{ adminText('workflowsAll.interests_3fc5473', 'Interests') }}</h2>
      <!--
        Enter commits a chip, so it must not bubble up and submit the whole form.
        There is no predefined item list either, hence the empty append-icon, and
        single-line drops the row height a labelled box field would reserve.
      -->
      <span @keydown.enter.prevent="">
        <v-combobox
          v-model="form.interests"
          :placeholder="adminText('workflowsAll.type_an_interest_and_press_enter_90fb34d', 'Type an interest and press Enter')"
          append-icon=""
          multiple
          chips
          deletable-chips
          box
          single-line
          hide-details
        />
      </span>
    </section>

    <section class="form-section">
      <h2 class="section-title">{{ adminText('workflowsAll.add_venue_map_3b3edff', 'Add Venue Map') }}</h2>
      <venue-map-field v-model="form.venueMap" />
    </section>

    <section class="form-section">
      <h2 class="section-title">{{ adminText('workflowsAll.color_selection_3a85951', 'Color Selection') }}</h2>
      <div class="section-card preview-layout">
        <!--
          The page and theme pickers only steer the preview; they are deliberately kept
          out of `form`, which is sent to the mutation as is.
        -->
        <div class="preview-layout__settings">
          <div class="setting">
            <p class="block-label">{{ adminText('workflowsAll.select_page_for_preview_890167e', 'Select page for preview') }}</p>
            <select-field
              v-model="previewPage"
              :items="workflowUi(previewPages)"
              :label="adminText('workflowsAll.page_fb06270', 'Page')"
              item-render="text"
              item-select="value"
              hide-details
            />
          </div>
          <div class="setting">
            <hue-picker-field
              v-model="form.primaryColor"
              :label="adminText('workflowsAll.primary_color_a1055f1', 'Primary color')"
              palette-preview
              :hint="adminText('workflowsAll.primary_brand_color_used_on_the_majority_of_e_ffc0852', 'Primary brand color, used on the majority of elements including buttons, headings, subheadings, links, and illustrations.')"
            />
          </div>
          <div class="setting">
            <p class="block-label">{{ adminText('workflowsAll.light_dark_theme_preview_adfd772', 'Light & dark theme preview') }}</p>
            <switch-field v-model="previewDark" class="mt-0 pt-0" />
          </div>
        </div>
        <div class="preview-layout__preview">
          <p class="block-label block-label--preview">{{ adminText('workflowsAll.app_preview_9edb618', 'App preview') }}</p>
          <app-preview :page="previewPage" :dark="previewDark" :primary-color="form.primaryColor" />
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import AppPreview from './previews/app-preview'
import CoverPreview from './previews/cover-preview'

// The app's tab bar, in its order. Each needs a light and dark SVG in public/app-previews.
const PREVIEW_PAGES = [
  { text: 'workflowsAll.home_70f8bb9', value: 'home' },
  { text: 'workflowsAll.agenda_891e9d6', value: 'agenda' },
  { text: 'workflowsAll.people_b37554f', value: 'people' },
  { text: 'workflowsAll.chat_2ced57f', value: 'chat' },
  { text: 'workflowsAll.more_4bab2d8', value: 'more' },
]

export default {
  mixins: [WorkflowDisplay],
  name: 'event-conference-data-form',
  components: { AppPreview, CoverPreview },
  props: {
    initialValues: {
      type: Object,
      default: () => ({}),
    },
    vs: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      form: this.initialValues || {},
      previewPages: PREVIEW_PAGES,
      previewPage: PREVIEW_PAGES[0].value,
      previewDark: false,
    }
  },
  watch: {
    form: {
      handler(val) {
        this.$emit('values', val)
      },
      deep: true,
    },
  },
}
</script>

<style scoped>
.form-section {
  padding-bottom: 40px;
}

.section-title {
  font-family: 'Raleway', sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: -0.6px;
  color: #333333;
  margin-bottom: 24px;
}

.section-card {
  border: 1px solid #d9d9d9;
  border-radius: 24px;
  background-color: #ffffff;
  padding: 25px;
}

.preview-layout {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
}

.preview-layout__settings {
  flex: 0 0 220px;
  max-width: 100%;
}

.preview-layout__preview {
  flex: 1 1 280px;
  /* The phone scales to whatever is left, so it must not claim its full 390 px. */
  min-width: 0;
  max-width: 390px;
}

.setting + .setting {
  margin-top: 24px;
}

.block-label {
  font-family: 'Raleway', sans-serif;
  font-size: 16px;
  font-weight: bold;
  line-height: 1.2;
  letter-spacing: -0.24px;
  color: #333333;
  margin-bottom: 16px;
}

.block-label--preview {
  margin-bottom: 24px;
}
</style>
